"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BulletOptions = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const slate_react_1 = require("slate-react");
const slate_1 = require("slate");
const ArgsPane_1 = require("@jsonjoy.com/ui/lib/4-card/ContextMenu/ArgsPane");
const context_1 = require("@jsonjoy.com/ui/lib/4-card/Popup/context");
const context_2 = require("@jsonjoy.com/ui/lib/styles/context");
const useSyncStore_1 = require("@jsonjoy.com/ui/lib/hooks/useSyncStore");
const Dot_1 = require("@jsonjoy.com/ui/lib/1-inline/Dot");
const BorderStyleIcon_1 = require("./BorderStyleIcon");
const colors_1 = require("./colors");
const settings_1 = require("./settings");
const numbers_1 = tslib_1.__importDefault(require("iconista/lib/react/tabler/numbers"));
const alert_triangle_1 = tslib_1.__importDefault(require("iconista/lib/react/tabler_filled/alert-triangle"));
const letter_case_1 = tslib_1.__importDefault(require("iconista/lib/react/tabler/letter-case"));
const eraser_1 = tslib_1.__importDefault(require("iconista/lib/react/tabler/eraser"));
const NumberIcon = (props) => (React.createElement(numbers_1.default, { width: 16, height: 16, ...props }));
const SymbolIcon = (props) => (React.createElement(alert_triangle_1.default, { width: 16, height: 16, ...props }));
const CharsIcon = (props) => (React.createElement(letter_case_1.default, { width: 16, height: 16, ...props }));
const EraserIcon = (props) => (React.createElement(eraser_1.default, { width: 16, height: 16, ...props }));
const INDICATOR_ICONS = {
    number: () => React.createElement(NumberIcon, null),
    symbol: () => React.createElement(SymbolIcon, null),
    chars: () => React.createElement(CharsIcon, null),
};
const StateIcon = ({ state }) => {
    const styles = (0, context_2.useStyles)();
    const colors = (0, colors_1.getStepStateColors)(styles, state);
    const glow = state === 'done' || state === 'warning' || state === 'error';
    return React.createElement(Dot_1.Dot, { color: colors.line, size: 10, glow: glow });
};
const STATE_ICONS = settings_1.STEP_STATES.reduce((acc, state) => {
    acc[state] = () => React.createElement(StateIcon, { state: state });
    return acc;
}, {});
const horizontalBorderIcon = (style) => () => React.createElement(BorderStyleIcon_1.BorderStyleIcon, { style: style });
const verticalBorderIcon = (style) => () => (React.createElement(BorderStyleIcon_1.BorderStyleIcon, { style: style, orientation: "vertical" }));
const RingRowIcon = () => (React.createElement("svg", { width: 16, height: 16, viewBox: "0 0 16 16", "aria-hidden": "true", style: { display: 'block' } },
    React.createElement("circle", { cx: 8, cy: 8, r: 4, fill: "none", stroke: "currentColor", strokeWidth: 1.6 })));
const HaloRowIcon = () => (React.createElement("svg", { width: 16, height: 16, viewBox: "0 0 16 16", "aria-hidden": "true", style: { display: 'block' } },
    React.createElement("circle", { cx: 8, cy: 8, r: 3, fill: "none", stroke: "currentColor", strokeWidth: 0.9 }),
    React.createElement("circle", { cx: 8, cy: 8, r: 6.4, fill: "none", stroke: "currentColor", strokeWidth: 1.6 })));
const ConnectorRowIcon = () => (React.createElement("svg", { width: 16, height: 16, viewBox: "0 0 16 16", "aria-hidden": "true", style: { display: 'block' } },
    React.createElement("line", { x1: 8, y1: 2, x2: 8, y2: 14, stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" })));
const BgRowIcon = () => (React.createElement("svg", { width: 16, height: 16, viewBox: "0 0 16 16", "aria-hidden": "true", style: { display: 'block' } },
    React.createElement("circle", { cx: 8, cy: 8, r: 5.5, fill: "currentColor", opacity: 0.35 })));
const ColorRowIcon = () => (React.createElement("svg", { width: 16, height: 16, viewBox: "0 0 16 16", "aria-hidden": "true", style: { display: 'block' } },
    React.createElement("text", { x: "8", y: "12", textAnchor: "middle", fontSize: "12", fontWeight: "700", fill: "currentColor" }, "A")));
const renderRingRowIcon = () => React.createElement(RingRowIcon, null);
const renderHaloRowIcon = () => React.createElement(HaloRowIcon, null);
const renderConnectorRowIcon = () => React.createElement(ConnectorRowIcon, null);
const renderBgRowIcon = () => React.createElement(BgRowIcon, null);
const renderColorRowIcon = () => React.createElement(ColorRowIcon, null);
const renderCharsRowIcon = () => React.createElement(CharsIcon, null);
const renderEraserIcon = () => React.createElement(EraserIcon, null);
const isDefaultable = (v) => !!v && typeof v === 'object' && 'def' in v;
const enumOpts = (values, label, icon) => values.map((v) => {
    const opt = { id: v, name: label[v] };
    if (icon)
        opt.icon = icon(v);
    return opt;
});
const hasStyleOverrides = (e) => e.stepCol !== undefined ||
    e.stepBg !== undefined ||
    e.ring !== undefined ||
    e.ringCol !== undefined ||
    e.ringWidth !== undefined ||
    e.halo !== undefined ||
    e.haloCol !== undefined ||
    e.haloWidth !== undefined ||
    e.line !== undefined ||
    e.lineCol !== undefined ||
    e.lineWidth !== undefined;
/**
 * Side-popup configuration pane for a single stepper bullet. Mirrors edits to
 * the slate document in real time as the user picks values — no Apply step.
 */
const BulletOptions = ({ element, closePopup }) => {
    const editor = (0, slate_react_1.useSlateStatic)();
    const styles = (0, context_2.useStyles)();
    const popup = (0, context_1.usePopup)();
    const onCancel = React.useCallback(() => {
        if (closePopup)
            closePopup();
        else
            popup?.close();
    }, [closePopup, popup]);
    const elementRef = React.useRef(element);
    elementRef.current = element;
    const item = React.useMemo(() => ({ name: 'Step options', compact: true }), []);
    const resetStyles = React.useCallback(() => {
        const e = elementRef.current;
        try {
            const path = slate_react_1.ReactEditor.findPath(editor, e);
            slate_1.Transforms.unsetNodes(editor, [
                'stepCol',
                'stepBg',
                'ring',
                'ringCol',
                'ringWidth',
                'halo',
                'haloCol',
                'haloWidth',
                'line',
                'lineCol',
                'lineWidth',
            ], { at: path });
        }
        catch { }
    }, [editor]);
    const setField = React.useCallback((field, value) => {
        const e = elementRef.current;
        try {
            const path = slate_react_1.ReactEditor.findPath(editor, e);
            if (value === undefined || value === '') {
                slate_1.Transforms.unsetNodes(editor, field, { at: path });
            }
            else {
                slate_1.Transforms.setNodes(editor, { [field]: value }, { at: path });
            }
        }
        catch { }
    }, [editor]);
    const params = React.useMemo(() => {
        const e = element;
        const list = [];
        const state = (0, settings_1.getStepState)(e.stepState);
        const colors = (0, colors_1.getStepStateColors)(styles, state);
        const lineHex = (0, colors_1.toHex)(colors.line);
        const bgHex = (0, colors_1.toHex)(colors.bg);
        const resolvedBg = e.stepBg ?? colors.bg;
        const autoGlyph = e.stepBg ? (0, colors_1.pickGlyphColor)(styles, resolvedBg, colors.glyph) : colors.glyph;
        const colHex = (0, colors_1.toHex)(autoGlyph);
        const indicator = (0, settings_1.getStepIndicator)(e.stepIndicator);
        const indicatorIsChars = indicator === 'chars';
        const ringHasSubControls = e.ring !== undefined && e.ring !== 'none';
        const haloHasSubControls = e.halo !== undefined && e.halo !== 'none';
        const lineHasSubControls = e.line !== undefined && e.line !== 'none';
        const stylesCollapsed = !hasStyleOverrides(e);
        list.push({ name: 'Behavior', heading: true, collapsible: true });
        list.push({
            kind: 'select',
            id: 'stepState',
            name: 'State',
            default: state,
            options: enumOpts(settings_1.STEP_STATES, settings_1.STEP_STATE_LABEL, (v) => STATE_ICONS[v]),
        });
        list.push({
            kind: 'select',
            id: 'stepIndicator',
            name: 'Indicator',
            defaultable: true,
            default: settings_1.DEF_INDICATOR,
            initialDef: !e.stepIndicator,
            initialValue: e.stepIndicator ?? settings_1.DEF_INDICATOR,
            options: enumOpts(settings_1.STEP_INDICATORS, settings_1.STEP_INDICATOR_LABEL, (v) => INDICATOR_ICONS[v]),
        });
        list.push({
            kind: 'char',
            id: 'stepChar',
            name: 'Chars',
            icon: renderCharsRowIcon,
            optional: true,
            length: 2,
            emoji: true,
            placeholder: '',
            default: e.stepChar ?? '',
            visible: (0, useSyncStore_1.syncStore)(indicatorIsChars),
        });
        list.push({ name: 'Styles', heading: true, collapsible: true, initialCollapsed: stylesCollapsed });
        list.push({
            kind: 'color',
            id: 'stepCol',
            name: 'Color',
            icon: renderColorRowIcon,
            defaultable: true,
            default: colHex,
            alpha: true,
            initialDef: e.stepCol === undefined,
            initialValue: e.stepCol ? (0, colors_1.toHex)(e.stepCol) : colHex,
        });
        list.push({
            kind: 'color',
            id: 'stepBg',
            name: 'Background',
            icon: renderBgRowIcon,
            defaultable: true,
            default: bgHex,
            alpha: true,
            initialDef: e.stepBg === undefined,
            initialValue: e.stepBg ? (0, colors_1.toHex)(e.stepBg) : bgHex,
        });
        list.push({ name: 'sep-bg', innerSep: true });
        list.push({
            kind: 'enum',
            id: 'ring',
            name: 'Ring',
            icon: renderRingRowIcon,
            defaultable: true,
            default: settings_1.DEF_RING,
            initialDef: e.ring === undefined,
            initialValue: e.ring ?? settings_1.DEF_RING,
            options: enumOpts(settings_1.LINE_STYLES, settings_1.LINE_STYLE_LABEL, horizontalBorderIcon),
        });
        list.push({
            kind: 'color',
            id: 'ringCol',
            name: 'Ring color',
            defaultable: true,
            default: lineHex,
            alpha: true,
            initialDef: e.ringCol === undefined,
            initialValue: e.ringCol ? (0, colors_1.toHex)(e.ringCol) : lineHex,
            visible: (0, useSyncStore_1.syncStore)(ringHasSubControls),
        });
        list.push({
            kind: 'num',
            id: 'ringWidth',
            name: 'Ring width',
            defaultable: true,
            default: settings_1.DEF_RING_WIDTH,
            min: 0,
            max: 6,
            step: 1,
            decimals: 0,
            dragSensitivity: 0.03,
            initialDef: e.ringWidth === undefined,
            initialValue: e.ringWidth ?? settings_1.DEF_RING_WIDTH,
            visible: (0, useSyncStore_1.syncStore)(ringHasSubControls),
        });
        list.push({ name: 'sep-ring', innerSep: true });
        list.push({
            kind: 'enum',
            id: 'halo',
            name: 'Halo',
            icon: renderHaloRowIcon,
            defaultable: true,
            default: settings_1.DEF_HALO,
            initialDef: e.halo === undefined,
            initialValue: e.halo ?? settings_1.DEF_HALO,
            options: enumOpts(settings_1.LINE_STYLES, settings_1.LINE_STYLE_LABEL, horizontalBorderIcon),
        });
        list.push({
            kind: 'color',
            id: 'haloCol',
            name: 'Halo color',
            defaultable: true,
            default: lineHex,
            alpha: true,
            initialDef: e.haloCol === undefined,
            initialValue: e.haloCol ? (0, colors_1.toHex)(e.haloCol) : lineHex,
            visible: (0, useSyncStore_1.syncStore)(haloHasSubControls),
        });
        list.push({
            kind: 'num',
            id: 'haloWidth',
            name: 'Halo width',
            defaultable: true,
            default: settings_1.DEF_HALO_WIDTH,
            min: 0,
            max: 6,
            step: 1,
            decimals: 0,
            dragSensitivity: 0.03,
            initialDef: e.haloWidth === undefined,
            initialValue: e.haloWidth ?? settings_1.DEF_HALO_WIDTH,
            visible: (0, useSyncStore_1.syncStore)(haloHasSubControls),
        });
        list.push({ name: 'sep-halo', innerSep: true });
        list.push({
            kind: 'enum',
            id: 'line',
            name: 'Connector',
            icon: renderConnectorRowIcon,
            defaultable: true,
            default: settings_1.DEF_LINE,
            initialDef: e.line === undefined,
            initialValue: e.line ?? settings_1.DEF_LINE,
            options: enumOpts(settings_1.LINE_STYLES, settings_1.LINE_STYLE_LABEL, verticalBorderIcon),
        });
        list.push({
            kind: 'color',
            id: 'lineCol',
            name: 'Connector color',
            defaultable: true,
            default: lineHex,
            alpha: true,
            initialDef: e.lineCol === undefined,
            initialValue: e.lineCol ? (0, colors_1.toHex)(e.lineCol) : lineHex,
            visible: (0, useSyncStore_1.syncStore)(lineHasSubControls),
        });
        list.push({
            kind: 'num',
            id: 'lineWidth',
            name: 'Connector width',
            defaultable: true,
            default: settings_1.DEF_LINE_WIDTH,
            min: 0,
            max: 6,
            step: 1,
            decimals: 0,
            dragSensitivity: 0.03,
            initialDef: e.lineWidth === undefined,
            initialValue: e.lineWidth ?? settings_1.DEF_LINE_WIDTH,
            visible: (0, useSyncStore_1.syncStore)(lineHasSubControls),
        });
        list.push({ name: 'Label', heading: true, collapsible: true, initialCollapsed: true });
        list.push({
            kind: 'str',
            id: 'stepTitle',
            name: 'Title',
            optional: true,
            placeholder: 'Shipping information',
            default: e.stepTitle ?? '',
        });
        list.push({
            kind: 'str',
            id: 'stepDesc',
            name: 'Description',
            optional: true,
            placeholder: 'Brief sub-text',
            default: e.stepDesc ?? '',
        });
        list.push({ name: 'Actions', heading: true, collapsible: true, initialCollapsed: true });
        list.push({
            kind: 'btn',
            id: 'reset',
            name: 'Reset styles',
            buttonLabel: 'Reset',
            buttonIcon: renderEraserIcon,
            danger: true,
            confirm: true,
            confirmLabel: 'Reset all style overrides?',
            confirmActionLabel: 'Reset',
            onClick: resetStyles,
        });
        return list;
    }, [element, styles, resetStyles]);
    const onChange = React.useCallback((_list, map) => {
        const state = (0, settings_1.getStepState)(map.stepState);
        setField('stepState', state === settings_1.DEF_STATE ? undefined : state);
        const indRaw = map.stepIndicator;
        let indicatorValue;
        if (isDefaultable(indRaw)) {
            indicatorValue = indRaw.def ? undefined : (0, settings_1.getStepIndicator)(indRaw.value);
        }
        else {
            const v = (0, settings_1.getStepIndicator)(indRaw);
            indicatorValue = v === settings_1.DEF_INDICATOR ? undefined : v;
        }
        setField('stepIndicator', indicatorValue);
        const charsRaw = map.stepChar ?? '';
        const chars = Array.from(charsRaw).slice(0, 2).join('');
        setField('stepChar', chars ? chars : undefined);
        setField('stepCol', readDefaultableColor(map.stepCol));
        setField('stepBg', readDefaultableColor(map.stepBg));
        // Style group: when style is in default mode, only clear the style
        // itself. Keep the previously-stored color/width so the values come
        // back when the user re-enables the override.
        applyStyleGroup(map, 'ring', setField);
        applyStyleGroup(map, 'halo', setField, 'haloCol', 'haloWidth');
        applyStyleGroup(map, 'line', setField, 'lineCol', 'lineWidth');
        setField('stepTitle', readOptionalStr(map.stepTitle));
        setField('stepDesc', readOptionalStr(map.stepDesc));
    }, [setField]);
    return React.createElement(ArgsPane_1.ArgsPane, { item: item, params: params, onCancel: onCancel, onChange: onChange, minWidth: 303 });
};
exports.BulletOptions = BulletOptions;
const readOptionalStr = (raw) => {
    if (typeof raw !== 'string')
        return undefined;
    return raw.trim() ? raw : undefined;
};
const readDefaultableColor = (raw) => {
    if (isDefaultable(raw)) {
        if (raw.def)
            return undefined;
        const v = String(raw.value ?? '').trim();
        return v ? (0, colors_1.toHex)(v) : undefined;
    }
    if (typeof raw === 'string' && raw.trim())
        return (0, colors_1.toHex)(raw.trim());
    return undefined;
};
const styleColField = (key) => key === 'ring' ? 'ringCol' : key === 'halo' ? 'haloCol' : 'lineCol';
const styleWidthField = (key) => key === 'ring' ? 'ringWidth' : key === 'halo' ? 'haloWidth' : 'lineWidth';
const applyStyleGroup = (map, key, setField, colField = styleColField(key), widthField = styleWidthField(key)) => {
    const styleRaw = map[key];
    let style;
    let overridden = false;
    if (isDefaultable(styleRaw)) {
        overridden = !styleRaw.def;
        if (overridden)
            style = (0, settings_1.getLineStyle)(styleRaw.value, 'solid');
    }
    else if (typeof styleRaw === 'string') {
        overridden = true;
        style = (0, settings_1.getLineStyle)(styleRaw, 'solid');
    }
    setField(key, style);
    if (!overridden)
        return;
    const colorRaw = map[colField];
    let color;
    if (isDefaultable(colorRaw)) {
        color = colorRaw.def ? undefined : toHexOrUndefined(colorRaw.value);
    }
    else if (typeof colorRaw === 'string' && colorRaw.trim()) {
        color = (0, colors_1.toHex)(colorRaw.trim());
    }
    setField(colField, color);
    const widthRaw = map[widthField];
    let width;
    if (isDefaultable(widthRaw)) {
        width = widthRaw.def ? undefined : (0, settings_1.clampWidth)(widthRaw.value);
    }
    else if (typeof widthRaw === 'number') {
        width = (0, settings_1.clampWidth)(widthRaw);
    }
    setField(widthField, width);
};
const toHexOrUndefined = (v) => {
    if (typeof v !== 'string')
        return undefined;
    const trimmed = v.trim();
    return trimmed ? (0, colors_1.toHex)(trimmed) : undefined;
};
